import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface ImageSearchPagination {
  current_page: number;
  per_page: number;
  total: number;
  has_more: boolean;
}

export interface ImageSearchState {
  results: any[];
  pagination: ImageSearchPagination | null;
  picRegionInfo: any | null;
  requestId: string | null;
  searchImagePreview: string | null;
  searchFileName: string | null;
  isLoading: boolean;
  hasSearched: boolean;
  error: string | null;
}

const STORAGE_KEY = "1412_image_search_cache";

const loadCachedSearch = (): Partial<ImageSearchState> => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return parsed;
      }
    }
  } catch (error) {
    console.error("Failed to load cached image search state:", error);
  }
  return {};
};

const cachedState = loadCachedSearch();

const initialState: ImageSearchState = {
  results: Array.isArray(cachedState.results) ? cachedState.results : [],
  pagination: cachedState.pagination || null,
  picRegionInfo: cachedState.picRegionInfo || null,
  requestId: cachedState.requestId || null,
  searchImagePreview: cachedState.searchImagePreview || null,
  searchFileName: cachedState.searchFileName || null,
  isLoading: false,
  hasSearched: Boolean(cachedState.results && cachedState.results.length > 0),
  error: null,
};

export const imageSearchSlice = createSlice({
  name: "imageSearch",
  initialState,
  reducers: {
    startImageSearch: (
      state,
      action: PayloadAction<{ previewUrl?: string; fileName?: string } | undefined>
    ) => {
      state.isLoading = true;
      state.error = null;
      state.hasSearched = true;
      if (action?.payload?.previewUrl) {
        state.searchImagePreview = action.payload.previewUrl;
      }
      if (action?.payload?.fileName) {
        state.searchFileName = action.payload.fileName;
      }
      try {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            results: state.results,
            pagination: state.pagination,
            picRegionInfo: state.picRegionInfo,
            requestId: state.requestId,
            searchImagePreview: state.searchImagePreview,
            searchFileName: state.searchFileName,
          })
        );
      } catch (err) {
        console.warn("Could not save to sessionStorage:", err);
      }
    },
    setImageSearchResults: (
      state,
      action: PayloadAction<{
        results: any[];
        pagination?: ImageSearchPagination | null;
        picRegionInfo?: any | null;
        requestId?: string | null;
        previewUrl?: string;
        fileName?: string;
      }>
    ) => {
      state.results = action.payload.results;
      state.pagination = action.payload.pagination ?? null;
      state.picRegionInfo = action.payload.picRegionInfo ?? null;
      state.requestId = action.payload.requestId ?? null;
      state.isLoading = false;
      state.hasSearched = true;
      state.error = null;
      if (action.payload.previewUrl) {
        state.searchImagePreview = action.payload.previewUrl;
      }
      if (action.payload.fileName) {
        state.searchFileName = action.payload.fileName;
      }
      try {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            results: state.results,
            pagination: state.pagination,
            picRegionInfo: state.picRegionInfo,
            requestId: state.requestId,
            searchImagePreview: state.searchImagePreview,
            searchFileName: state.searchFileName,
          })
        );
      } catch (err) {
        console.warn("Could not save to sessionStorage:", err);
      }
    },
    appendImageSearchResults: (
      state,
      action: PayloadAction<{
        results: any[];
        pagination?: ImageSearchPagination | null;
      }>
    ) => {
      state.results = [...state.results, ...action.payload.results];
      if (action.payload.pagination) {
        state.pagination = action.payload.pagination;
      }
      state.isLoading = false;
      try {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            results: state.results,
            pagination: state.pagination,
            picRegionInfo: state.picRegionInfo,
            requestId: state.requestId,
            searchImagePreview: state.searchImagePreview,
            searchFileName: state.searchFileName,
          })
        );
      } catch (err) {
        console.warn("Could not save to sessionStorage:", err);
      }
    },
    setImageSearchError: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    clearImageSearch: (state) => {
      state.results = [];
      state.pagination = null;
      state.picRegionInfo = null;
      state.requestId = null;
      state.searchImagePreview = null;
      state.searchFileName = null;
      state.isLoading = false;
      state.hasSearched = false;
      state.error = null;
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (err) {
        console.warn("Could not remove from sessionStorage:", err);
      }
    },
  },
});

export const {
  startImageSearch,
  setImageSearchResults,
  appendImageSearchResults,
  setImageSearchError,
  clearImageSearch,
} = imageSearchSlice.actions;

export default imageSearchSlice.reducer;
